/**
 * مواقعُ العمل وتسجيلُ الحضور منها.
 *
 * الحسابُ هنا لا في الخادم وحده: الشاشةُ تقول للموظّف «أنت على بُعد
 * ثمانين مترًا من المقرّ» قبل أن يضغط، فلا يضغط ليُردّ. والخادمُ يعيد
 * الحساب على كلّ حال — ما يُرسله المتصفّح رقمٌ يكتبه من شاء.
 */

/* ==========================================================================
   المسافة
   ========================================================================== */

/** نصفُ قطر الأرض بالأمتار — المتوسّط الكرويّ المعتمد. */
const EARTH_RADIUS_M = 6_371_008.8;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * المسافةُ بين نقطتين بالأمتار.
 *
 * صيغةُ هافرساين على كرةٍ لا على مجسَّم: الفرقُ بينهما دون نصف بالمئة،
 * وهو أقلُّ بكثيرٍ من خطأ تحديد المتصفّح نفسِه — فدقّةٌ أعلى هنا زينةٌ
 * تُكلّف ولا تغيّر قرارًا.
 */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return Math.round(2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h))));
}

export interface SitePoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  radiusMeters: number;
}

export interface SiteMatch {
  site: SitePoint;
  meters: number;
  inside: boolean;
}

/**
 * أقربُ موقعٍ إلى نقطة.
 *
 * يُردّ الأقربُ ولو كان بعيدًا: الرسالةُ «أقربُ موقعٍ لك المقرّ على بُعد
 * ٤٠٠ متر» تقول للموظّف ما يفعل، و«خارج النطاق» وحدها لا تقول شيئًا.
 */
export function nearestSite(
  point: { lat: number; lng: number },
  sites: readonly SitePoint[],
): SiteMatch | null {
  let best: SiteMatch | null = null;

  for (const site of sites) {
    const meters = distanceMeters(point, site);
    const match: SiteMatch = {
      site,
      meters,
      inside: meters <= site.radiusMeters,
    };

    if (!best) {
      best = match;
      continue;
    }

    /*
     * الداخلُ يسبق الخارجَ مهما كانت المسافة.
     *
     * موقعٌ نصفُ قطره واسعٌ وأنت داخله، وآخرُ أقربُ إليك وأنت خارجه:
     * الأوّلُ هو الجواب — والترتيبُ بالمسافة وحدَها كان يردّ حضورًا صحيحًا.
     */
    if (match.inside !== best.inside) {
      if (match.inside) best = match;
      continue;
    }

    if (match.meters < best.meters) best = match;
  }

  return best;
}

/* ==========================================================================
   حدود ما يُقبل
   ========================================================================== */

/**
 * أسوأُ دقّةٍ تُقبل، بالأمتار.
 *
 * تحديدٌ بدقّة كيلومترٍ ليس تحديدًا: يعني أنّ المتصفّح خمّن من عنوان
 * الإنترنت لا من القمر الصناعيّ، فيمرّ من يجلس في بيته على أنّه في
 * المكتب. والرقمُ سخيٌّ لأنّ التحديدَ داخل المباني يسوء فعلًا.
 */
export const MAX_ACCURACY_METERS = 500;

/** أوسعُ نصفِ قطرٍ يُقبل لموقع: ما فوقه لا يبقى تحديدًا. */
export const MAX_SITE_RADIUS = 5_000;
export const MIN_SITE_RADIUS = 25;

/* ==========================================================================
   حالة اليوم
   ========================================================================== */

export const CHECK_STATES = ['OUT', 'IN', 'DONE', 'OFF'] as const;
export type CheckState = (typeof CHECK_STATES)[number];

export interface CheckStateDef {
  key: CheckState;
  label: string;
  action: string | null;
  kind: 'info' | 'success' | 'warning' | 'danger' | 'neutral' | 'brand';
}

export const CHECK_STATE_DEFS: readonly CheckStateDef[] = [
  {
    key: 'OUT',
    label: 'لم تسجّل حضورك',
    action: 'سجّل حضورك',
    kind: 'brand',
  },
  { key: 'IN', label: 'أنت على رأس العمل', action: 'سجّل انصرافك', kind: 'success' },
  { key: 'DONE', label: 'انصرفتَ اليوم', action: null, kind: 'neutral' },
  { key: 'OFF', label: 'اليوم عطلة', action: null, kind: 'info' },
] as const;

export const CHECK_STATE_MAP = Object.fromEntries(
  CHECK_STATE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CheckState, CheckStateDef>>;
